import { useEffect, useRef } from 'react'
import { getWidgetCable, releaseWidgetCable } from '@/lib/widgetCable'
import type { Consumer, Subscription } from '@rails/actioncable'

export interface NewMessageNotification {
  id: number
  body: string
  channel: string
  conversation_id: number
  author_type?: 'Agent' | 'EndUser' | null
  author_name: string
  author_avatar_url: string | null
  author?: {
    id: number | null
    name: string
    avatar_url?: string | null
    online?: boolean
  }
  created_at?: string
  attachments?: Array<{
    id: number
    signed_id: string
    filename: string
    content_type: string
    byte_size: number
    url: string
    is_video?: boolean
    is_image?: boolean
  }>
  system_message_type?: 'away_notice' | 'email_capture' | 'rating_request' | 'scheduling' | null
  metadata?: Record<string, unknown> | null
}

export interface MessageDeletedNotification {
  message_id: number
  conversation_id: number
}

interface BroadcastData {
  type: 'new_message' | 'message_deleted'
  message?: NewMessageNotification
  message_id?: number
  conversation_id?: number
}

// Presence heartbeat cadence. The inbox shows a visitor as online for 3
// minutes after their last activity, so one heartbeat every 2 minutes keeps
// the indicator green while the tab is visible and lets it expire on its own
// once the visitor leaves. Nothing is sent while the tab is hidden.
export const PRESENCE_INTERVAL_MS = 2 * 60 * 1000

interface UseEndUserChannelProps {
  apiUrl: string
  sessionToken: string
  onNewMessage: (message: NewMessageNotification) => void
  onMessageDeleted?: (data: MessageDeletedNotification) => void
  onReconnect?: () => void
}

export function useEndUserChannel({
  apiUrl,
  sessionToken,
  onNewMessage,
  onMessageDeleted,
  onReconnect,
}: UseEndUserChannelProps) {
  const cableRef = useRef<Consumer | null>(null)
  const subscriptionRef = useRef<Subscription | null>(null)
  const presenceTimerRef = useRef<number | null>(null)
  const lastPresenceAtRef = useRef(0)

  // Use refs for callbacks to avoid recreating subscription
  const onNewMessageRef = useRef(onNewMessage)
  const onMessageDeletedRef = useRef(onMessageDeleted)
  const onReconnectRef = useRef(onReconnect)

  useEffect(() => {
    onNewMessageRef.current = onNewMessage
    onMessageDeletedRef.current = onMessageDeleted
    onReconnectRef.current = onReconnect
  }, [onNewMessage, onMessageDeleted, onReconnect])

  useEffect(() => {
    if (!sessionToken || !apiUrl) return

    // Get shared cable consumer (session token passed via subscription params, not URL)
    cableRef.current = getWidgetCable(apiUrl)

    // Send a heartbeat only if the previous one is old enough, so returning to
    // the tab or reconnecting does not fire an extra one right away. perform()
    // returns false when the socket is not open; that attempt does not count.
    function sendPresenceIfDue(): void {
      const now = Date.now()
      if (now - lastPresenceAtRef.current < PRESENCE_INTERVAL_MS) return
      if (subscriptionRef.current?.perform('presence')) {
        lastPresenceAtRef.current = now
      }
    }

    function stopPresence(): void {
      if (presenceTimerRef.current !== null) {
        window.clearTimeout(presenceTimerRef.current)
        presenceTimerRef.current = null
      }
    }

    // Schedule the next heartbeat relative to the last one that went out, not
    // to now: after a hide/show or a reconnect a fixed interval would restart
    // from zero and the gap could exceed the 3-minute online window. When the
    // heartbeat is due but the socket refused it, wait a full interval so a
    // closed socket cannot spin; connected() restarts the cadence anyway.
    function scheduleNextPresence(): void {
      const remaining = PRESENCE_INTERVAL_MS - (Date.now() - lastPresenceAtRef.current)
      const delay = remaining > 0 ? remaining : PRESENCE_INTERVAL_MS
      presenceTimerRef.current = window.setTimeout(() => {
        presenceTimerRef.current = null
        sendPresenceIfDue()
        scheduleNextPresence()
      }, delay)
    }

    function startPresence(): void {
      if (document.visibilityState === 'hidden') return
      if (presenceTimerRef.current !== null) return
      sendPresenceIfDue()
      scheduleNextPresence()
    }

    function handleVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        stopPresence()
      } else {
        startPresence()
      }
    }

    // Subscribe to the end user channel with session token for authentication
    let hasConnected = false
    const subscription = cableRef.current.subscriptions.create(
      {
        channel: 'EndUserChannel',
        session_token: sessionToken,
      },
      {
        connected() {
          startPresence()
          if (hasConnected) onReconnectRef.current?.()
          hasConnected = true
        },
        disconnected() {
          stopPresence()
        },
        received(data: BroadcastData) {
          if (data.type === 'new_message' && data.message) {
            onNewMessageRef.current(data.message)
          } else if (data.type === 'message_deleted' && data.message_id && data.conversation_id && onMessageDeletedRef.current) {
            onMessageDeletedRef.current({ message_id: data.message_id, conversation_id: data.conversation_id })
          }
        },
      }
    )

    subscriptionRef.current = subscription
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup: unsubscribe when component unmounts
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopPresence()
      subscription.unsubscribe()
      subscriptionRef.current = null
      releaseWidgetCable()
      cableRef.current = null
    }
  }, [apiUrl, sessionToken])
}
